import { openAppointmentDemandForm } from "@/src/app/appointments/appointmentsUtils";
import { closeDrawer, openDrawer } from "@/src/components/dialogs";

import { Card, CompartmentCard } from "@/src/components/cards";
import { FetchableList } from "@/src/components/fetchableLists";
import StaggeredAnimation from "@/src/components/staggeredAnimationComponent";
import { UploadCards } from "@/src/components/uploadSpots";
import {
  Appointment,
  AppointmentType,
  Patient,
  appointmentStatuses,
  refusalCategories,
} from "@/src/data/allTypes";
import { USER } from "@/src/data/globalVariables";
import apiFetch from "@/src/utils/apiFetch";
import {
  formatDate,
  getItemId,
  getServiceId,
  getUserId,
  getUserShortName,
  isHospital,
  openToast,
  uid,
} from "@/src/utils/utils";
import { useState } from "react";

import { CalendarPage } from "../calendar/calendarPage";
import { AppointmentForm } from "../appointments/appointmentForm";
import { openAppointmentActionsDialog } from "../appointments/appointmentActions";
import moment from "moment";

function AppointmentsHistory({
  title,
  showPastAppointments = true,
  types,
  patient,
  listOnly,
  onClick,
}: {
  title: string;
  showPastAppointments?: boolean;
  types: AppointmentType[] | null | undefined;
  patient: Patient;
  listOnly?: boolean;
  onClick?: () => void;
}) {
  const [reload, setReload] = useState(0);

  async function onAppointmentClick(
    index: number,
    allAppointments: Appointment[]
  ) {
    let appointment = allAppointments[index];
    openAppointmentActionsDialog({
      appointment,
      index,
      appointments: allAppointments,
      onChange: (newValue) => {
        if (!allAppointments) return;
        allAppointments[index] = newValue;
        setReload((prev) => prev + 1);
      },
    });

    try {
      await apiFetch("appointments", "markAppointmentAsRead", {
        appointmentId: appointment.id,
        userId: getUserId(),
      });
    } catch (e) {}
  }

  let list = (
    <FetchableList
      queryKey={["AppointmentsHistory", types, patient.id, reload]}
      queryFn={() =>
        apiFetch("appointments", "loadMany", {
          patientId: patient.id,
          appointmentTypesIds: types?.map((type) => type.id || ""),
          hasProtocol: false,
          sortObj: { start: "DESC" },
        })
      }
      emptyChild={
        <div className="center tcenter p30 c1 gap10">
          <div className="gray center">Aucun rendez-vous trouvé</div>
          {types && types.length != 0 && (
            <div className="gray">
              Dans{" "}
              {types
                .map((type) => {
                  return type.name;
                })
                ?.join(", ")}
            </div>
          )}
        </div>
      }
    >
      {(data) => {
        const futureAppointments = data?.filter(
          (appointment) => !moment(appointment.start).isBefore(moment())
        );
        const pastAppointments = data?.filter(
          (appointment) =>
            appointment.start && moment(appointment.start).isBefore(moment())
        );
        return (
          <div className="grid c1 gap20">
            <div className="c1 gap15">
              {futureAppointments?.map((appointment, index) => {
                return (
                  <StaggeredAnimation key={uid()} index={index}>
                    <AppointmentCard
                      current={appointment}
                      onClick={async () => {
                        await onAppointmentClick(index, futureAppointments);
                      }}
                    ></AppointmentCard>
                  </StaggeredAnimation>
                );
              })}
            </div>

            <div className="c1 gap15">
              {pastAppointments?.length != 0 && showPastAppointments && (
                <div className="bold gray">Rendez-vous passés</div>
              )}
              {showPastAppointments &&
                pastAppointments?.map((appointment, index) => {
                  return (
                    <StaggeredAnimation key={uid()} index={index}>
                      <AppointmentCard
                        current={appointment}
                        onClick={async () => {
                          await onAppointmentClick(index, pastAppointments);
                        }}
                      ></AppointmentCard>
                    </StaggeredAnimation>
                  );
                })}
            </div>
          </div>
        );
      }}
    </FetchableList>
  );

  if (listOnly) return list;

  return (
    <CompartmentCard
      color="blue"
      title={title}
      onClick={async () => {
        let appointment: Appointment = {
          patient,
          doctor: null,
          demandContent: null,
          start: null,
          end: null,
          anchorDate: null,
          type: null,
          service: getServiceId(),
          creator: getUserId(),
          demandService: getServiceId(),
          emergencyLevel: "none",
          isHospitalized: false,
          isUrgent: false,
          status: "pending",
          item: null,
          readBy: [],
        };

        await openAppointmentFromAndCalendar({
          appointment,
          patient,
          onConfirm: (appointment) => {
            setReload((prev) => prev + 1);
          },
        });
      }}
    >
      {list}
    </CompartmentCard>
  );
}

function AppointmentCard({
  current,
  onDelete,
  onClick,
}: {
  current: Appointment;
  onDelete?: () => void;
  onClick?: () => void;
}) {
  let [appointment, setAppointment] = useState<Appointment>(current);
  let [isLoading, setIsLoading] = useState(false);

  if (appointment.start && appointment.status == "pending")
    appointment.status = "scheduled";

  const statusInfo = appointmentStatuses[appointment.status || "pending"];

  const refusalInfo = appointment.refusalCategory
    ? refusalCategories[appointment.refusalCategory]
    : null;

  const isPast = moment(appointment.start).isBefore(moment());

  function shoudShowBackground() {
    if (isPast) return false;
    if (appointment.status == "finished") return true;
    if (appointment.isUrgent) return true;
    if (appointment.isHospitalized) return true;
    return false;
  }

  return (
    <div
      className={`${isLoading == true ? "disabled" : ""} `}
      style={{
        opacity: isPast ? "0.6" : undefined,
      }}
    >
      <Card
        key={uid()}
        color={shoudShowBackground() ? statusInfo?.type : "simple"}
        leading={<></>}
        title={
          <div>
            <strong>{appointment?.type?.name}</strong>
            {appointment.isHospitalized == true && (
              <span className="br2 blueish p5 mleft5">Hospitalisé</span>
            )}
            {appointment.isUrgent == true && (
              <span className="br2 redish p5 mleft5">Urgent</span>
            )}
            <span className={`${statusInfo?.type}ish br p5 mleft5`}>
              {statusInfo?.name}
            </span>
          </div>
        }
        onClick={async () => {
          console.log("appointment", appointment);
          setIsLoading(true);
          await onClick?.();

          setIsLoading(false);
        }}
        onDelete={onDelete}
      >
        <div className="c1 gap5 mtop5">
          {appointment.doctor != null && (
            <div className="bold">{getUserShortName(appointment.doctor)}</div>
          )}

          <div className="">{appointment.notes}</div>
          {appointment._date && (
            <div className="gray">
              Demandé le{" "}
              <strong>{formatDate(appointment._date, "LL à HH:mm")}</strong>
            </div>
          )}
          {appointment.start && (
            <div className="">
              Programmé pour le{" "}
              <strong>{formatDate(appointment.start, "LL à HH:mm")}</strong>
            </div>
          )}

          <div style={{ maxWidth: "500px" }}>
            {appointment.status == "finished" ? (
              <></>
            ) : (
              <UploadCards uploads={appointment.uploads} count={5} />
            )}
          </div>
          {appointment.status == "refused" && (
            <div className="redish br p10 mtop5">
              <div>
                Motif de refus: <strong>{refusalInfo?.name}</strong>
              </div>

              {appointment.refusalNotes?.trim() != "Cher confrère," && (
                <div>{appointment.refusalNotes}</div>
              )}
            </div>
          )}
        </div>
      </Card>
    </div>
  );
}

export async function openAppointmentFromAndCalendar({
  appointment,
  patient,
  onConfirm,
}: {
  appointment: Appointment;
  patient: Patient;
  onConfirm: (appointment: Appointment) => void;
}) {
  function canSchedule(type: AppointmentType | null | undefined) {
    if (!type) return false;
    if (!isHospital()) return true;
    console.log("USER.get()?.canScheduleTypes", {
      canScheduleTypes: USER.get()?.canScheduleTypes,
      type,
    });
    for (let authorizedType of USER.get()?.canScheduleTypes || []) {
      if (getItemId(authorizedType) == getItemId(type)) return true;
    }
    return false;
  }

  await openDrawer({
    id: "appointmentDrawer",
    title: "Ajouter un rendez-vous",
    content: (
      <AppointmentForm
        user={null}
        patient={appointment.patient}
        current={appointment}
        onChange={(newValue) => {
          appointment = newValue;
        }}
      />
    ),
    onConfirm: async () => {
      if (!appointment.patient) return openToast("error", "Patient manquant");
      if (!appointment.type) return openToast("error", "Type manquant");
      appointment = await openAppointmentDemandForm(appointment);

      if (canSchedule(appointment.type)) {
        await openDrawer({
          id: "calendarDrawer",
          title: "Calendrier",
          additionalClasses: ["big"],
          content: (
            <CalendarPage
              userId={getItemId(appointment.doctor)}
              patient={patient}
              onDateSelect={(start, end, doctorId) => {
                appointment.status = "scheduled";
                appointment.start = start;
                appointment.end = end;
                appointment.doctor = getItemId(doctorId);
                closeDrawer("calendarDrawer");
              }}
            />
          ),
        });
        if (!appointment.start) return;
      }

      appointment = await apiFetch("appointments", "addOne", {
        appointment,
      });

      await apiFetch("appointments", "changeAppointmentStatus", {
        appointmentId: appointment.id,
        status: appointment.status,
        userId: getUserId(),
      });
      onConfirm(appointment);
      openToast("success", "Elément ajouté avec succès");
      if (!canSchedule(appointment.type)) {
        openToast(
          "success",
          "Vous n'avez pas les permissions nécessaires pour programmer le rendez-vous dans un date.\n\n Le rendez-vous a été ajouté comme demande. \n\nPour avoir les permissions nécessaires, contactez l'administrateur."
        );
      }
    },
  });
}

export { AppointmentCard, AppointmentsHistory };
